<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'company',
    path: '/companies/:id',
    component: 'Company',
    params: [
      {
        name: 'id',
        prop: 'oid',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Company',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
    role: {
      type: String,
      required: false,
    },
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    create: {
      own: $fn.value.pgettext('Company|Header', 'New own company'),
      partner: $fn.value.pgettext('Company|Header', 'New partner'),
    },
    parent: {
      own: $fn.value.pgettext('Company|Header', 'Companies'),
      partner: $fn.value.pgettext('Company|Header', 'Partners'),
    },
    delete: {
      own: $fn.value.pgettext('Company|Header', 'Delete own company'),
      partner: $fn.value.pgettext('Company|Header', 'Delete partner'),
    },
  });
  const tabManager = ref(null);
  const item = ref(null);
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noHeader: true,
    contentClass: 'p-0',
    noPrimaryButton: true,
    noCloseButton: true,
    size: 'full-screen',
  }));

  const $data = computed(() => ({
    messages: messages.value,
    tabManager: tabManager.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_tabManager = () => {
    return new $app.value.utils.LazyTabManager();
  };
  const $$load_item = () => {
    return $fn.value.fetch('read_company', {
      parameters: { id: props.oid },
      asResource: true,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'tabManager' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { tabManager: $$load_tabManager, item: $$load_item },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      messages,
      tabManager,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const effectiveRole = computed(() => {
    if (loaded.value) {
      return item.value ? item.value.role || props.role : props.role;
    }
    return null;
  });
  const parentRouteName = computed(() => {
    return effectiveRole.value == 'own' ? 'app.ownCompanies' : 'app.partners';
  });
  const isOwnCompany = computed(() => {
    return effectiveRole.value === 'own';
  });
  const isPartnerCompany = computed(() => {
    return effectiveRole.value === 'partner';
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          props.oid == null && messages.value.create[props.role],
          item.value && item.value.snapshot('name'),
        ],
        icon: $config.value.company.icons[props.role || item.value.role],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.role,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('role');
    }
  );
  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_5bujgd4xe_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_5bujgd4xe =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_5bujgd4xe_getActionArgs,
    });
  const inst_evh_7315092382398562_close_1kr1e2hd9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_5bujgd4xe,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_1kr1e2hd9],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ucovjjuom =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_bo67vil0u =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ucovjjuom,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_bo67vil0u],
    event: 'reload',
  });
  const act_2248226175642056_closeModal_9yt03guol =
    new actionTypes.CloseModalAction({});
  const inst_evh_2248226175642056_close_j874tdxu6 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_closeModal_9yt03guol,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_2248226175642056_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_close_j874tdxu6],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_rh4gq6tlw =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop_zs2qgdiql =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_rh4gq6tlw,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_6466705138206980_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6466705138206980_clickPreventStop_zs2qgdiql],
      event: 'click.prevent.stop',
    });
  const act_8320016629450276_closeComponent_vhr82tzj8 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8320016629450276_success_6sbyk2tuc =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_closeComponent_vhr82tzj8,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_8320016629450276_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8320016629450276_success_6sbyk2tuc],
    event: 'success',
  });
  const act_6705964765214521_reloadComponentData_whffm9ef5 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6705964765214521_success_w7ourdvag =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_reloadComponentData_whffm9ef5,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6705964765214521_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6705964765214521_success_w7ourdvag],
    event: 'success',
  });
  const act_5477420070495986_reloadComponentData_d6674oivj =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5477420070495986_reload_mxu2kkbjj =
    new actionTypes.EventHandlerImpl({
      action: act_5477420070495986_reloadComponentData_d6674oivj,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5477420070495986_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5477420070495986_reload_mxu2kkbjj],
    event: 'reload',
  });
  const act_6337614691287098_reloadComponentData_sz5cnpqju =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6337614691287098_reload_ug8pmyjks =
    new actionTypes.EventHandlerImpl({
      action: act_6337614691287098_reloadComponentData_sz5cnpqju,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6337614691287098_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6337614691287098_reload_ug8pmyjks],
    event: 'reload',
  });
  const act_2406129194387753_reloadComponentData_3jkdzpog6 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2406129194387753_reload_a7uck5zgl =
    new actionTypes.EventHandlerImpl({
      action: act_2406129194387753_reloadComponentData_3jkdzpog6,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2406129194387753_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2406129194387753_reload_a7uck5zgl],
    event: 'reload',
  });
  const act_1840158740026147_reloadComponentData_dvz2mpryi =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1840158740026147_reload_hrqzs01j3 =
    new actionTypes.EventHandlerImpl({
      action: act_1840158740026147_reloadComponentData_dvz2mpryi,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_1840158740026147_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1840158740026147_reload_hrqzs01j3],
    event: 'reload',
  });
  const act_4071170783051919_reloadComponentData_up02hpmzh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_4071170783051919_reload_v2ubsmcet =
    new actionTypes.EventHandlerImpl({
      action: act_4071170783051919_reloadComponentData_up02hpmzh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_4071170783051919_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4071170783051919_reload_v2ubsmcet],
    event: 'reload',
  });
  const act_7713630524832656_reloadComponentData_cfxcxyek9 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7713630524832656_reload_h1rkuw54l =
    new actionTypes.EventHandlerImpl({
      action: act_7713630524832656_reloadComponentData_cfxcxyek9,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7713630524832656_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7713630524832656_reload_h1rkuw54l],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_1kr1e2hd9,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_bo67vil0u,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_close_j874tdxu6,
    inst_evh_2248226175642056_close,
    inst_evh_6466705138206980_clickPreventStop_zs2qgdiql,
    reload: inst_evh_6466705138206980_clickPreventStop_zs2qgdiql,
    inst_evh_6466705138206980_clickPreventStop,
    inst_evh_8320016629450276_success_6sbyk2tuc,
    inst_evh_8320016629450276_success,
    inst_evh_6705964765214521_success_w7ourdvag,
    inst_evh_6705964765214521_success,
    inst_evh_5477420070495986_reload_mxu2kkbjj,
    inst_evh_5477420070495986_reload,
    inst_evh_6337614691287098_reload_ug8pmyjks,
    inst_evh_6337614691287098_reload,
    inst_evh_2406129194387753_reload_a7uck5zgl,
    inst_evh_2406129194387753_reload,
    inst_evh_1840158740026147_reload_hrqzs01j3,
    inst_evh_1840158740026147_reload,
    inst_evh_4071170783051919_reload_v2ubsmcet,
    inst_evh_4071170783051919_reload,
    inst_evh_7713630524832656_reload_h1rkuw54l,
    inst_evh_7713630524832656_reload,
  };

  const evh_7315092382398562_close_1kr1e2hd9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_1kr1e2hd9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_bo67vil0u = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_bo67vil0u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close_j874tdxu6 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_j874tdxu6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop_zs2qgdiql = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop_zs2qgdiql.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success_6sbyk2tuc = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success_6sbyk2tuc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_success = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_success_w7ourdvag = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_success_w7ourdvag.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_success = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5477420070495986_reload_mxu2kkbjj = async (event, scope) => {
    return await actions.inst_evh_5477420070495986_reload_mxu2kkbjj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5477420070495986_reload = async (event, scope) => {
    return await actions.inst_evh_5477420070495986_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6337614691287098_reload_ug8pmyjks = async (event, scope) => {
    return await actions.inst_evh_6337614691287098_reload_ug8pmyjks.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6337614691287098_reload = async (event, scope) => {
    return await actions.inst_evh_6337614691287098_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2406129194387753_reload_a7uck5zgl = async (event, scope) => {
    return await actions.inst_evh_2406129194387753_reload_a7uck5zgl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2406129194387753_reload = async (event, scope) => {
    return await actions.inst_evh_2406129194387753_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1840158740026147_reload_hrqzs01j3 = async (event, scope) => {
    return await actions.inst_evh_1840158740026147_reload_hrqzs01j3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1840158740026147_reload = async (event, scope) => {
    return await actions.inst_evh_1840158740026147_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4071170783051919_reload_v2ubsmcet = async (event, scope) => {
    return await actions.inst_evh_4071170783051919_reload_v2ubsmcet.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4071170783051919_reload = async (event, scope) => {
    return await actions.inst_evh_4071170783051919_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7713630524832656_reload_h1rkuw54l = async (event, scope) => {
    return await actions.inst_evh_7713630524832656_reload_h1rkuw54l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7713630524832656_reload = async (event, scope) => {
    return await actions.inst_evh_7713630524832656_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Company"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <MaterialTabs
      :closeButton="modal"
      :tabManager="tabManager"
      :title="item.snapshot('name')"
      :parentText="messages.parent[effectiveRole]"
      :parentTo="{ name: parentRouteName }"
      class="h-100"
      v-if="loaded"
      @close="evh_2248226175642056_close($event, {})"
    >
      <template #actions>
        <ItpButton
          variant="light"
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_6466705138206980_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <BtnDeleteResource
          :resource="item"
          :dialogHeader="messages.delete[effectiveRole]"
          @success="evh_8320016629450276_success($event, {})"
        >
        </BtnDeleteResource>
        <BtnSyncResource
          :resource="item"
          size="sm"
          @success="evh_6705964765214521_success($event, {})"
        >
        </BtnSyncResource>
      </template>
      <BTab :title="gettext('General')" class="container-lg float-start">
        <ItpRow>
          <ItpCol :w="6">
            <UcCompanyGeneral
              :item="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_5477420070495986_reload($event, {})"
            >
            </UcCompanyGeneral>
            <UcContactList
              :parent="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_6337614691287098_reload($event, {})"
            >
            </UcContactList>
            <UcEmployeeList
              :parent="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_2406129194387753_reload($event, {})"
            >
            </UcEmployeeList>
            <UcCompanyFormContracts
              :company="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_1840158740026147_reload($event, {})"
            >
            </UcCompanyFormContracts>
          </ItpCol>
          <ItpCol :w="6">
            <UcAddressList
              :parent="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_4071170783051919_reload($event, {})"
            >
            </UcAddressList>
            <UcCompanyBankAccounts
              :parent="item"
              :reload="lastLoaded"
              class="pb-4"
              @reload="evh_7713630524832656_reload($event, {})"
            >
            </UcCompanyBankAccounts>
            <UcCommentsList :parent="item" :reload="lastLoaded" class="pb-4">
            </UcCommentsList>
            <UcAttachmentsList :parent="item" :reload="lastLoaded" class="pb-4">
            </UcAttachmentsList>
          </ItpCol>
        </ItpRow>
      </BTab>
      <BTab
        :title="pgettext('Company', 'Billing list')"
        class="container-fluid h-100"
      >
        <UcBillingList
          :companyId="isOwnCompany ? item.id : null"
          :clientId="isPartnerCompany ? item.id : null"
          :reload="lastLoaded"
          class="h-100"
          v-if="effectiveRole && tabManager.visited(1)"
        >
        </UcBillingList>
      </BTab>
      <BTab
        :title="pgettext('Company', 'Invoices')"
        class="container-fluid h-100"
      >
        <InvoiceLinesView
          :companyId="isOwnCompany ? item.id : null"
          :partnerId="isPartnerCompany ? item.id : null"
          :reload="lastLoaded"
          class="h-100"
          v-if="effectiveRole && tabManager.visited(2)"
        >
        </InvoiceLinesView>
      </BTab>
      <BTab
        :title="pgettext('Company', 'Payments')"
        class="container-fluid h-100"
      >
        <PaymentsView
          :parameters="{
            company: isOwnCompany ? item.id : null,
            partner: isPartnerCompany ? item.id : null,
          }"
          :reload="lastLoaded"
          class="h-100"
          v-if="effectiveRole && tabManager.visited(3)"
        >
        </PaymentsView>
      </BTab>
      <BTab
        :title="pgettext('Company', 'Statement')"
        class="container-fluid h-100"
      >
        <UcCompanyStatement
          :oid="item.id"
          :reload="lastLoaded"
          class="h-100"
          v-if="effectiveRole && tabManager.visited(4)"
        >
        </UcCompanyStatement>
      </BTab>
      <BTab
        :title="pgettext('Company', 'Accounts')"
        class="container-fluid h-100"
        v-if="isOwnCompany"
      >
        <AccountsView
          :companyId="item.id"
          :reload="lastLoaded"
          class="h-100"
          v-if="tabManager.visited(5)"
        >
        </AccountsView>
      </BTab>
    </MaterialTabs>
  </ItpPage>
</template>
